/*
 * © 2017 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
import { createSelector } from "reselect";
import { Machine } from "@centralwebteam/narwhal";
import { RootState } from "..";
import {
  VerdictGroup,
  JobPresentation,
  mapVerdictToGroup,
} from "@/presentation/Job";
import { JobPerformance, JobType } from "../state";
import {
  getStartDate,
  isValidStartToken,
  selectActiveFilters,
  selectMachinesInvariant,
} from "../selectors";
import { getRowId } from "@/handlers/JobPerformance/functions";

// #region internal filtering functions
const filterMachines = (
  machines: Machine[],
  locationIds: string[],
  machineTypes: string[],
  verdicts: VerdictGroup[],
  jobs: JobPresentation[]
) =>
  machines.filter(
    (m) =>
      (!locationIds?.length ||
        locationIds.includes(m.locationId!) ||
        (locationIds.includes("") && !m.locationId)) &&
      (!machineTypes?.length || machineTypes.includes(m.type)) &&
      (!verdicts?.length ||
        jobs.some(
          (j) => j.machineId === m.id && verdicts.includes(mapVerdictToGroup(j))
        ))
  );

const filterJobTypes = (
  jobTypes: JobType[],
  machines: Machine[],
  jobName: string,
  verdicts: VerdictGroup[],
  jobs: JobPresentation[]
) => {
  return jobTypes.filter(
    (jt) =>
      (!jobName || jt.name.toLowerCase().includes(jobName.toLowerCase())) &&
      machines.find((m) => jt.machines && jt.machines.includes(m.id)) &&
      (!verdicts?.length ||
        jobs.some(
          (j) => j.name === jt.name && verdicts.includes(mapVerdictToGroup(j))
        ))
  );
};

const getFilteredJobCountsAndDurations = (
  jobPerformances: JobPerformance[],
  verdicts: VerdictGroup[]
) => {
  let counts = {} as Mappy<number>;
  let durations = {} as Mappy<number>;

  for (const jp of jobPerformances) {
    if (!verdicts?.length) {
      counts = { ...counts, [jp.name]: jp.count.total };
      durations = { ...durations, [jp.name]: jp.duration.total };
    } else {
      let count = 0,
        duration = 0;
      for (const vg of verdicts) {
        switch (vg) {
          case "Fail":
            count += jp.count.fail;
            duration += jp.duration.fail;
            break;
          case "Pass":
            count += jp.count.pass;
            duration += jp.duration.pass;
            break;
          case "No Verdict":
            count += jp.count.incomplete;
            duration += jp.duration.incomplete;
            break;
        }
      }
      counts = { ...counts, [jp.name]: count };
      durations = { ...durations, [jp.name]: duration };
    }
  }

  return { filteredJobCounts: counts, filteredJobDurations: durations };
};
// #endregion

// #region selectors
export const selectMachinePerformances = (s: RootState) =>
  s.jobPerformance.machinePerformances;

/** Selects the metadata-related parts { metadataPerformance, activeMetadata, extendedSummaryFetched } from jobPerformance state */
export const selectJobPerformanceMetadata = createSelector(
  [
    (s: RootState) => s.jobPerformance.metadataPerformance,
    (s: RootState) => s.jobPerformance.activeMetadata,
    (s: RootState) => s.jobPerformance.extendedSummaryFetched,
    (s: RootState) => s.jobPerformance.metadataNamesList,
  ],
  (
    metadataPerformance,
    activeMetadata,
    extendedSummaryFetched,
    metadataNamesList
  ) => ({
    metadataPerformance,
    activeMetadata,
    extendedSummaryFetched,
    metadataNamesList,
  })
);

/** Selects all sorting parameters from jobPerformance state */
export const selectJobPerformanceSorting = createSelector(
  [
    (s: RootState) => s.jobPerformance.detailSortColumn,
    (s: RootState) => s.jobPerformance.detailSortDirection,
    (s: RootState) => s.jobPerformance.machineJobAggregateSortColumn,
    (s: RootState) => s.jobPerformance.machineJobAggregateSortDirection,
    (s: RootState) => s.jobPerformance.metadataJobAggregateSortColumn,
    (s: RootState) => s.jobPerformance.metadataJobAggregateSortDirection,
    (s: RootState) => s.jobPerformance.sortColumn,
    (s: RootState) => s.jobPerformance.sortDirection,
    (s: RootState) => s.jobPerformance.filter,
  ],
  (
    detailSortColumn,
    detailSortDirection,
    machineJobAggregateSortColumn,
    machineJobAggregateSortDirection,
    metadataJobAggregateSortColumn,
    metadataJobAggregateSortDirection,
    sortColumn,
    sortDirection,
    filter
  ) => ({
    detailSortColumn,
    detailSortDirection,
    machineJobAggregateSortColumn,
    machineJobAggregateSortDirection,
    metadataJobAggregateSortColumn,
    metadataJobAggregateSortDirection,
    sortColumn,
    sortDirection,
    filter,
  })
);

/** Selects {openJobs, openMachines, openMetadataValues} from jobPerformance state */
export const selectJobPerformanceLayout = createSelector(
  [
    (s: RootState) => s.jobPerformance.openJobs,
    (s: RootState) => s.jobPerformance.openMachines,
    (s: RootState) => s.jobPerformance.openMetadataValues,
  ],
  (openJobs, openMachines, openMetadataValues) => ({
    openJobs,
    openMachines,
    openMetadataValues,
  })
);

export const selectWork = ({ jobPerformance }: RootState) =>
  jobPerformance.work;
export const selectActiveVerdictFilter = (s: RootState) =>
  s.filter.active.verdict as VerdictGroup[];
export const selectJobTypePerformances = (s: RootState) =>
  s.jobPerformance.jobTypePerformances;
export const selectJobTypes = (s: RootState) => s.jobPerformance.jobTypes;
const selectJobSummaries = (s: RootState) => s.jobPerformance.jobSummaries;
const selectOpenJobs = (s: RootState) => s.jobPerformance.openJobs;
export const selectOpenMachines = (s: RootState) =>
  s.jobPerformance.openMachines;

export const selectFilteredJobSummaries = createSelector(
  [
    (s: RootState) => s.filter.active.verdict,
    (s: RootState) => s.jobPerformance.jobSummaries,
  ],
  (verdicts: VerdictGroup[], jobs: JobPresentation[]) => {
    if (!verdicts?.length)
      return jobs.map((j) => {
        if (
          j.type === "JobEquatorSummaryExtended" ||
          j.type === "JobMachineToolSummaryExtended" ||
          j.type === "JobCoordinateMeasurementSummaryExtended" ||
          j.type === "metrology"
        )
          return { ...j, type: "metrology" };
        else return j;
      });

    return jobs
      .filter(
        (j) =>
          (j.type === "JobEquatorSummaryExtended" ||
            j.type === "JobMachineToolSummaryExtended" ||
            j.type === "JobCoordinateMeasurementSummaryExtended" ||
            j.type === "metrology") &&
          verdicts.includes(mapVerdictToGroup(j))
      )
      .map((m) => {
        return { ...m, type: "metrology" };
      });
  }
);

export const selectFilteredMachines = createSelector(
  [selectMachinesInvariant, selectActiveFilters, selectJobSummaries],
  (machines, filters, summaries) =>
    filterMachines(
      machines,
      filters.locationId,
      filters.machineType,
      filters.verdict as VerdictGroup[],
      summaries
    )
);

export const selectFilteredJobTypes = createSelector(
  [
    selectJobTypes,
    selectFilteredMachines,
    selectActiveFilters,
    selectJobSummaries,
  ],
  (jobTypes, machines, filters, summaries) =>
    filterJobTypes(
      jobTypes,
      machines,
      (filters.name || [""])[0],
      filters.verdict as VerdictGroup[],
      summaries
    )
);

export const selectJobCountsAndDurations = createSelector(
  [selectJobTypePerformances, selectActiveFilters],
  (performances, filters) =>
    getFilteredJobCountsAndDurations(
      Object.values(performances),
      filters.verdict as VerdictGroup[]
    )
);

export const selectAllExpanded = createSelector(
  [
    selectFilteredJobTypes,
    selectOpenJobs,
    selectOpenMachines,
    (s: RootState) => s.jobPerformance.openMetadataValues,
    (s: RootState) => s.jobPerformance.activeMetadata,
  ],
  (
    filteredJobTypes,
    openJobs,
    openMachines,
    openMetadataValues,
    activeMetaData
  ) => {
    const state =
      activeMetaData === "Measuring Machine"
        ? filteredJobTypes.every(
            (f) =>
              openJobs.includes(f.name) &&
              [...f.machines]
                .map((m) => getRowId(f, { id: m }))
                .every((jm) => openMachines.includes(jm))
          )
        : filteredJobTypes.every(
            (f) =>
              openJobs.includes(f.name) &&
              f.metadataNames.every((md) =>
                openMetadataValues.includes(f.name + md)
              )
          );
    return state;
  }
);

export const selectStartAndEnd = createSelector(
  [(s: RootState) => s.params],
  ({ start, end }) => {
    return [
      isValidStartToken(start) ? getStartDate(start) : new Date(start),
      end ?? new Date().toISOString(),
    ];
  }
);

export const selectLastFetchedForRefresh = (state: RootState) =>
  state.jobPerformance.lastFetchedForRefresh;

// #endregion
